import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeftRight,
  Download,
  ListChecks,
  RefreshCcw,
  Search,
  Settings,
  X,
} from 'lucide-react';
import clsx from 'clsx';
import { apiRequest, ApiError } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { useToast } from '../lib/toast';
import { ListShell } from '../components/ListShell';
import { Button } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import { Skeleton } from '../components/ui/Skeleton';
import { Drawer } from '../components/ui/Drawer';
import { Pagination } from '../components/people/Pagination';
import { GatedSection } from '../components/shared/GatedSection';
import { EditIconButton } from '../components/shared/EditIconButton';
import { ListOrOutlet } from '../components/shared/ListOrOutlet';
import { Sparkline } from '../components/shared/Sparkline';
import {
  CompareTable,
  ConfigForm,
  HrCommentsThread,
  LeaderboardTable,
  PeriodReports,
  ProbationHistoryPanel,
  ScoreCard,
  VariableBreakdown,
  PERFORMANCE_BAND_LABEL,
  type LeaderboardSortBy,
  type LeaderboardSortOrder,
  type PerformanceBandValue,
  type PerformanceBreakdownEnvelope,
  type PerformanceConfigResponse,
  type PerformanceLeaderboardItem,
  type PerformanceLeaderboardResponse,
  type PerformanceRecomputeResult,
  type PerformanceSnapshotEnvelope,
  type PerformanceTrendResponse,
} from '../components/performance';
import { formatDate } from '../lib/format';
import type { ListResponse } from '../lib/api';

export const Route = createFileRoute('/_authed/hr/performance')({
  component: HrPerformancePage,
});

type Tab = 'leaderboard' | 'compare' | 'config';

const TAB_OPTIONS: ReadonlyArray<{
  value: Tab;
  label: string;
  icon: React.ReactNode;
  superAdminOnly?: boolean;
}> = [
  {
    value: 'leaderboard',
    label: 'Leaderboard',
    icon: <ListChecks size={14} aria-hidden="true" />,
  },
  {
    value: 'compare',
    label: 'Compare',
    icon: <ArrowLeftRight size={14} aria-hidden="true" />,
  },
  {
    value: 'config',
    label: 'Score settings',
    icon: <Settings size={14} aria-hidden="true" />,
    superAdminOnly: true,
  },
];

const PAGE_SIZE = 25;

function HrPerformancePage() {
  const hasRole = useAuthStore((s) => s.hasRole);
  const isSuperAdmin = hasRole('super_admin');
  // Per §4.27 / detail-pages-and-enrollment-plan: HR_ROLES gate the pencil
  // shortcut that opens the legacy in-place drawer. Row click navigation
  // is available to anyone who can read the leaderboard.
  const canManage = hasRole('super_admin', 'academic_admin', 'nalim');
  const [tab, setTab] = useState<Tab>('leaderboard');

  const visibleTabs = useMemo(
    () =>
      TAB_OPTIONS.filter((t) => !t.superAdminOnly || isSuperAdmin),
    [isSuperAdmin],
  );

  return (
    <ListOrOutlet basePath="/hr/performance">
    <ListShell
      title="Performance scores"
      description="Composite Top / Medium / Average ranking over the last 90 days, sourced from every module."
    >
      <div
        role="tablist"
        aria-label="Performance score sections"
        className="flex flex-wrap gap-1 border-b border-[color:var(--color-border)]"
      >
        {visibleTabs.map((opt) => {
          const active = tab === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setTab(opt.value)}
              className={clsx(
                'inline-flex items-center gap-2 rounded-t-md px-4 py-2 text-sm font-medium transition-colors',
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500',
                active
                  ? 'border-b-2 border-gold-500 text-[color:var(--color-text)]'
                  : 'text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]',
              )}
            >
              {opt.icon}
              {opt.label}
            </button>
          );
        })}
      </div>

      <div className="mt-5">
        {tab === 'leaderboard' ? (
          <LeaderboardTab
            isSuperAdmin={isSuperAdmin}
            canManage={canManage}
          />
        ) : null}
        {tab === 'compare' ? <CompareTab /> : null}
        {tab === 'config' && isSuperAdmin ? <ConfigTab /> : null}
      </div>
    </ListShell>
    </ListOrOutlet>
  );
}

// =========================================================================
// Leaderboard tab
// =========================================================================

function LeaderboardTab({
  isSuperAdmin,
  canManage,
}: {
  isSuperAdmin: boolean;
  canManage: boolean;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<LeaderboardSortBy>('score');
  const [sortOrder, setSortOrder] = useState<LeaderboardSortOrder>('desc');
  const [bandFilter, setBandFilter] = useState<PerformanceBandValue | 'all'>('all');
  const [includeWarmingUp, setIncludeWarmingUp] = useState(false);
  const [includeHidden, setIncludeHidden] = useState(false);
  // Drawer state is preserved for the pencil-icon power-user shortcut.
  // Row click now navigates to the detail page instead of opening it.
  const [selectedTeacher, setSelectedTeacher] = useState<
    PerformanceLeaderboardItem | null
  >(null);

  const queryParams = useMemo(() => {
    const out: Record<string, string | number> = {
      page,
      pageSize: PAGE_SIZE,
      sortBy,
      sortOrder,
      includeWarmingUp: String(includeWarmingUp),
      includeHidden: String(includeHidden),
    };
    if (bandFilter !== 'all') out.band = bandFilter;
    return out;
  }, [page, sortBy, sortOrder, bandFilter, includeWarmingUp, includeHidden]);

  const listQuery = useQuery({
    queryKey: ['performance', 'leaderboard', queryParams] as const,
    queryFn: ({ signal }) =>
      apiRequest<PerformanceLeaderboardResponse>(
        '/api/performance/leaderboard',
        { signal, query: queryParams },
      ),
    placeholderData: (prev) => prev,
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return failureCount < 2;
    },
  });

  const recomputeMutation = useMutation({
    mutationFn: async () =>
      apiRequest<PerformanceRecomputeResult>(
        '/api/performance/snapshots/recompute',
        { method: 'POST', body: {} },
      ),
    onSuccess: (result) => {
      toast(
        `Recomputed ${result.count} teacher${result.count === 1 ? '' : 's'}${
          result.errors > 0 ? ` · ${result.errors} error${result.errors === 1 ? '' : 's'}` : ''
        }.`,
        { variant: result.errors > 0 ? 'info' : 'success' },
      );
      void queryClient.invalidateQueries({ queryKey: ['performance'] });
    },
    onError: (err) =>
      toast(
        err instanceof ApiError ? err.message : 'Could not recompute snapshots.',
        { variant: 'error' },
      ),
  });

  const handleExport = () => {
    const params = new URLSearchParams();
    if (includeWarmingUp) params.set('includeWarmingUp', 'true');
    if (includeHidden) params.set('includeHidden', 'true');
    const apiOrigin =
      (import.meta.env.VITE_API_ORIGIN as string | undefined) ??
      'https://api.babulilmonline.com';
    const url = `${apiOrigin}/api/performance/export${
      params.toString() ? `?${params.toString()}` : ''
    }`;
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = '';
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  };

  const handleSortChange = (next: {
    sortBy: LeaderboardSortBy;
    sortOrder: LeaderboardSortOrder;
  }) => {
    setSortBy(next.sortBy);
    setSortOrder(next.sortOrder);
    setPage(1);
  };

  const items = listQuery.data?.items ?? [];

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <BandFilterChips
          value={bandFilter}
          onChange={(next) => {
            setBandFilter(next);
            setPage(1);
          }}
        />
        <label className="ml-2 inline-flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            checked={includeWarmingUp}
            onChange={(e) => {
              setIncludeWarmingUp(e.target.checked);
              setPage(1);
            }}
            className="h-3.5 w-3.5 accent-gold-500"
            disabled={bandFilter !== 'all'}
          />
          <span className="text-[color:var(--color-text-muted)]">Include warming up</span>
        </label>
        <label className="inline-flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            checked={includeHidden}
            onChange={(e) => {
              setIncludeHidden(e.target.checked);
              setPage(1);
            }}
            className="h-3.5 w-3.5 accent-gold-500"
            disabled={bandFilter !== 'all'}
          />
          <span className="text-[color:var(--color-text-muted)]">Include hidden</span>
        </label>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {isSuperAdmin ? (
            <Button
              variant="secondary"
              size="sm"
              icon={<RefreshCcw size={14} aria-hidden="true" />}
              onClick={() => recomputeMutation.mutate()}
              loading={recomputeMutation.isPending}
            >
              Recompute snapshots
            </Button>
          ) : null}
          <Button
            variant="secondary"
            size="sm"
            icon={<Download size={14} aria-hidden="true" />}
            onClick={handleExport}
            disabled={(listQuery.data?.total ?? 0) === 0}
          >
            Export CSV
          </Button>
        </div>
      </div>

      <GatedSection
        error={listQuery.error}
        title="Performance scores"
        onRetry={() => listQuery.refetch()}
      >
        {listQuery.isLoading ? (
          <Skeleton className="h-72 w-full" />
        ) : items.length === 0 ? (
          <EmptyState
            title="No teachers match this filter"
            description={
              bandFilter !== 'all'
                ? `No teachers landed in the ${PERFORMANCE_BAND_LABEL[bandFilter]} band on the latest snapshot.`
                : 'Once the nightly cron writes its first snapshot, ranked teachers will appear here.'
            }
          />
        ) : (
          <>
            {listQuery.data?.snapshotDate ? (
              <p className="text-xs text-[color:var(--color-text-muted)]">
                Snapshot date:{' '}
                <span className="font-medium text-[color:var(--color-text)]">
                  {formatDate(listQuery.data.snapshotDate)}
                </span>
                <span className="ml-3">
                  {listQuery.data.total} teacher
                  {listQuery.data.total === 1 ? '' : 's'} in this view
                </span>
              </p>
            ) : null}
            <LeaderboardTable
              items={items}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortChange={handleSortChange}
              onRowClick={(row) =>
                navigate({
                  to: '/hr/performance/teachers/$teacherId',
                  params: { teacherId: row.teacherId },
                })
              }
              rankOffset={(page - 1) * PAGE_SIZE}
              rowActionsLabel="Quick view"
              renderRowActions={
                canManage
                  ? (row) => (
                      <EditIconButton
                        label={`Quick view ${row.teacherName ?? 'teacher'}`}
                        title="Quick view (in-place drawer)"
                        onClick={() => setSelectedTeacher(row)}
                      />
                    )
                  : undefined
              }
            />
            <Pagination
              page={page}
              pageSize={PAGE_SIZE}
              total={listQuery.data?.total ?? 0}
              onPageChange={setPage}
            />
          </>
        )}
      </GatedSection>

      <TeacherDetailDrawer
        teacher={selectedTeacher}
        onClose={() => setSelectedTeacher(null)}
      />
    </section>
  );
}

interface BandFilterChipsProps {
  value: PerformanceBandValue | 'all';
  onChange: (next: PerformanceBandValue | 'all') => void;
}

function BandFilterChips({ value, onChange }: BandFilterChipsProps) {
  // We only surface the three ranked bands as quick chips. The warming-up
  // and hidden bands are gated behind the explicit "include" toggles to
  // avoid drowning the queue in non-ranked rows.
  const RANKED_BANDS: ReadonlyArray<PerformanceBandValue> = [
    'top',
    'medium',
    'average',
  ];
  return (
    <div role="group" aria-label="Filter by band" className="flex items-center gap-1">
      <Chip
        active={value === 'all'}
        onClick={() => onChange('all')}
        label="All"
      />
      {RANKED_BANDS.map((band) => (
        <Chip
          key={band}
          active={value === band}
          onClick={() => onChange(band)}
          label={PERFORMANCE_BAND_LABEL[band]}
        />
      ))}
    </div>
  );
}

function Chip({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={clsx(
        'inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500',
        active
          ? 'border-gold-500 bg-gold-50 text-gold-900 dark:bg-gold-700/20 dark:text-gold-50'
          : 'border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]',
      )}
    >
      {label}
    </button>
  );
}

// =========================================================================
// Teacher detail drawer
// =========================================================================

interface TeacherDetailDrawerProps {
  teacher: PerformanceLeaderboardItem | null;
  onClose: () => void;
}

function TeacherDetailDrawer({ teacher, onClose }: TeacherDetailDrawerProps) {
  const open = teacher !== null;
  const teacherId = teacher?.teacherId ?? null;

  const breakdownQuery = useQuery({
    queryKey: ['performance', 'breakdown', teacherId] as const,
    enabled: Boolean(teacherId),
    queryFn: ({ signal }) =>
      apiRequest<PerformanceBreakdownEnvelope>(
        `/api/performance/teachers/${encodeURIComponent(teacherId!)}/breakdown`,
        { signal },
      ),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return failureCount < 2;
    },
  });

  const trendQuery = useQuery({
    queryKey: ['performance', 'trend', teacherId] as const,
    enabled: Boolean(teacherId),
    queryFn: ({ signal }) =>
      apiRequest<PerformanceTrendResponse>(
        `/api/performance/teachers/${encodeURIComponent(teacherId!)}/trend`,
        { signal, query: { days: 30 } },
      ),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return failureCount < 2;
    },
  });

  return (
    <Drawer
      open={open}
      onClose={onClose}
      size="lg"
      title={teacher?.teacherName ?? 'Teacher detail'}
    >
      {teacher ? (
        <div className="space-y-5">
          {breakdownQuery.isLoading || !breakdownQuery.data ? (
            <Skeleton className="h-32 w-full" />
          ) : !breakdownQuery.data.snapshot ? (
            <p className="text-sm text-[color:var(--color-text-muted)]">
              No snapshot has been written for this teacher yet.
            </p>
          ) : (
            <ScoreCard snapshot={breakdownQuery.data.snapshot} />
          )}

          {/*
            Onboarding period (M-6). Gated on the probation summary alone, not
            on the snapshot — an onboarding teacher accrues weekly reviews
            before their first nightly TMA snapshot exists, and that is exactly
            when this history is most useful. HR-only deep-link is supplied
            because this drawer already sits behind the `canManage` gate.
          */}
          {teacherId ? (
            <ProbationHistoryPanel
              probation={breakdownQuery.data?.probation}
              historyTeacherId={teacherId}
            />
          ) : null}

          {breakdownQuery.data?.snapshot ? (
            <section>
              <h3 className="mb-3 font-display text-sm font-semibold">
                Breakdown by metric
              </h3>
              <VariableBreakdown
                metrics={breakdownQuery.data.snapshot.metrics}
              />
            </section>
          ) : null}

          <section>
            <h3 className="mb-3 font-display text-sm font-semibold">
              Last 30 days
            </h3>
            {trendQuery.isLoading || !trendQuery.data ? (
              <Skeleton className="h-32 w-full" />
            ) : trendQuery.data.items.length === 0 ? (
              <p className="text-sm text-[color:var(--color-text-muted)]">
                No trend data yet.
              </p>
            ) : (
              <Sparkline
                points={trendQuery.data.items
                  .slice()
                  .sort((a, b) => a.snapshotDate.localeCompare(b.snapshotDate))
                  .map((item) => ({
                    x: item.snapshotDate,
                    y: item.score,
                    title: `${formatDate(item.snapshotDate)} · ${item.score}/100 · ${item.band}`,
                  }))}
                gridlines={[33, 66]}
                ariaLabel={`Score trend for ${teacher.teacherName ?? 'teacher'}`}
              />
            )}
          </section>

          {teacherId ? <PeriodReports teacherId={teacherId} /> : null}

          {teacherId ? <HrCommentsThread teacherId={teacherId} /> : null}

          <div className="flex justify-end pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      ) : null}
    </Drawer>
  );
}

// =========================================================================
// Compare tab
// =========================================================================

interface TeacherSearchRow {
  id: string;
  name: string;
  employeeId: string | null;
}

function CompareTab() {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [searchText, setSearchText] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);

  const searchQuery = useQuery({
    queryKey: ['teachers', 'search', searchText] as const,
    enabled: searchOpen && searchText.trim().length >= 1,
    queryFn: ({ signal }) =>
      apiRequest<ListResponse<TeacherSearchRow>>('/api/teachers', {
        signal,
        query: { q: searchText.trim(), pageSize: 20 },
      }),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return failureCount < 2;
    },
  });

  // We fetch each selected teacher's latest snapshot independently. The
  // snapshot endpoint is per-teacher, so we issue one query per selection
  // — at the 4-teacher cap this is cheap and gives us per-row retry.
  const snapshotQueries = useQuery({
    queryKey: ['performance', 'compare', selectedIds] as const,
    enabled: selectedIds.length > 0,
    queryFn: async ({ signal }) => {
      const results = await Promise.all(
        selectedIds.map((teacherId) =>
          apiRequest<PerformanceSnapshotEnvelope>(
            `/api/performance/teachers/${encodeURIComponent(teacherId)}/snapshot`,
            { signal },
          ).catch((err: unknown) => {
            if (err instanceof ApiError && err.status === 404) return null;
            throw err;
          }),
        ),
      );
      return results;
    },
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return failureCount < 2;
    },
  });

  const snapshots = useMemo(() => {
    if (!snapshotQueries.data) return [];
    return snapshotQueries.data
      .map((envelope) => envelope?.snapshot ?? null)
      .filter((s): s is NonNullable<typeof s> => s !== null);
  }, [snapshotQueries.data]);

  const addTeacher = (row: TeacherSearchRow) => {
    if (selectedIds.includes(row.id)) return;
    if (selectedIds.length >= 4) return;
    setSelectedIds([...selectedIds, row.id]);
    setSearchText('');
    setSearchOpen(false);
  };

  const removeTeacher = (teacherId: string) => {
    setSelectedIds(selectedIds.filter((id) => id !== teacherId));
  };

  const searchResults = searchQuery.data?.items ?? [];

  return (
    <section className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0 max-w-md flex-1">
          <label
            htmlFor="compare-search"
            className="block text-sm font-medium"
          >
            Add teachers to compare
          </label>
          <p className="text-xs text-[color:var(--color-text-muted)]">
            Pick 2 to 4 teachers. The latest snapshot for each is shown
            side-by-side.
          </p>
          <div className="relative mt-1.5">
            <span
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--color-text-muted)]"
              aria-hidden="true"
            >
              <Search size={16} />
            </span>
            <input
              id="compare-search"
              type="search"
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setSearchOpen(true);
              }}
              onFocus={() => setSearchOpen(true)}
              placeholder="Search by name or employee ID"
              autoComplete="off"
              className="w-full rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] py-2 pl-9 pr-3 text-sm focus:border-gold-500 focus:outline-none focus:ring-1 focus:ring-gold-500"
              disabled={selectedIds.length >= 4}
            />
            {searchOpen && searchText.trim().length > 0 ? (
              <div
                role="listbox"
                aria-label="Teacher search results"
                className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] shadow-lg"
              >
                {searchQuery.isLoading ? (
                  <p className="p-3 text-xs text-[color:var(--color-text-muted)]">
                    Searching…
                  </p>
                ) : searchResults.length === 0 ? (
                  <p className="p-3 text-xs text-[color:var(--color-text-muted)]">
                    No matches.
                  </p>
                ) : (
                  searchResults.map((row) => {
                    const already = selectedIds.includes(row.id);
                    return (
                      <button
                        key={row.id}
                        type="button"
                        role="option"
                        aria-selected={already}
                        disabled={already}
                        onClick={() => addTeacher(row)}
                        className={clsx(
                          'flex w-full items-center justify-between gap-3 border-b border-[color:var(--color-border)] px-3 py-2 text-left text-sm last:border-b-0',
                          already
                            ? 'cursor-not-allowed bg-[color:var(--color-surface)] text-[color:var(--color-text-muted)]'
                            : 'hover:bg-[color:var(--color-surface)]',
                        )}
                      >
                        <span className="font-medium">{row.name}</span>
                        <span className="text-xs text-[color:var(--color-text-muted)]">
                          {row.employeeId ?? '—'}
                          {already ? ' · selected' : ''}
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
            ) : null}
          </div>
        </div>
        {selectedIds.length > 0 ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            icon={<X size={14} aria-hidden="true" />}
            onClick={() => setSelectedIds([])}
          >
            Clear selection
          </Button>
        ) : null}
      </header>

      {selectedIds.length === 0 ? (
        <EmptyState
          title="Pick 2 to 4 teachers"
          description="Search above to add teachers. Their per-metric scores will line up side-by-side."
          icon={<ArrowLeftRight size={32} aria-hidden="true" />}
        />
      ) : snapshotQueries.isLoading ? (
        <Skeleton className="h-72 w-full" />
      ) : snapshotQueries.error ? (
        <GatedSection
          error={snapshotQueries.error}
          title="Performance scores"
          onRetry={() => void snapshotQueries.refetch()}
        >
          {null}
        </GatedSection>
      ) : snapshots.length === 0 ? (
        <EmptyState
          title="No snapshots for the selected teachers"
          description="None of these teachers have a snapshot yet — check back after tonight's run."
        />
      ) : (
        <CompareTable snapshots={snapshots} onRemove={removeTeacher} />
      )}
    </section>
  );
}

// =========================================================================
// Config tab (super_admin only)
// =========================================================================

function ConfigTab() {
  const configQuery = useQuery({
    queryKey: ['performance', 'config'] as const,
    queryFn: ({ signal }) =>
      apiRequest<PerformanceConfigResponse>('/api/performance/config', {
        signal,
      }),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return failureCount < 2;
    },
  });

  return (
    <section className="space-y-4">
      <GatedSection
        error={configQuery.error}
        title="Score settings"
        onRetry={() => configQuery.refetch()}
      >
        {configQuery.isLoading ? (
          <Skeleton className="h-96 w-full" />
        ) : !configQuery.data?.config ? (
          <EmptyState
            title="No active config yet"
            description="The default config will be seeded on the first read or first PATCH."
          />
        ) : (
          <>
            <header className="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] px-4 py-3 text-xs text-[color:var(--color-text-muted)]">
              <span>
                Active since{' '}
                <span className="font-medium text-[color:var(--color-text)]">
                  {formatDate(configQuery.data.config.effectiveFrom, {
                    withTime: true,
                  })}
                </span>
              </span>
            </header>
            <ConfigForm config={configQuery.data.config} />
          </>
        )}
      </GatedSection>

      <p className="text-xs text-[color:var(--color-text-muted)]">
        Saving creates a new config; today&apos;s snapshots will be
        recomputed at the next nightly cron, but yesterday&apos;s snapshots
        stay unchanged.
      </p>
    </section>
  );
}
